import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useSearchParams } from "react-router-dom";
import {
  LeadQualifyingQuestionsResponse,
  qualifyingQuestionsService,
} from "../services/qualifyingQuestionsService";

interface QualifyingQuestionsForm {
  currentVisa: string;
  timeline: string;
  goals: string;
  location: string;
}

interface LeadIdentity {
  leadNumber: string;
  firstName: string;
  lastName: string;
  email: string;
}

const UUID_PATTERN = /^[0-9a-f]{8}-[0-9a-f]{4}-[1-5][0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/i;

const isUuid = (value: string): boolean => UUID_PATTERN.test(value.trim());

const extractApiError = (error: unknown, fallback: string): string => {
  if (
    typeof error === "object" &&
    error !== null &&
    "response" in error &&
    typeof (error as { response?: { data?: { detail?: string } } }).response?.data?.detail ===
      "string"
  ) {
    return (error as { response?: { data?: { detail?: string } } }).response?.data?.detail ?? fallback;
  }

  if (error instanceof Error && error.message) {
    return error.message;
  }

  return fallback;
};

const firstQueryValue = (searchParams: URLSearchParams, keys: string[]): string => {
  for (const key of keys) {
    const value = searchParams.get(key)?.trim();
    if (value) return value;
  }
  return "";
};

const formatSubmittedAt = (value: string | null): string | null => {
  if (!value) return null;
  const parsed = new Date(value);
  if (Number.isNaN(parsed.getTime())) return value;
  return parsed.toLocaleString();
};

const mapResponseToForm = (
  response: LeadQualifyingQuestionsResponse,
  fallback: QualifyingQuestionsForm
): QualifyingQuestionsForm => ({
  currentVisa: response.current_visa ?? fallback.currentVisa,
  timeline: response.timeline ?? fallback.timeline,
  goals: response.goals ?? fallback.goals,
  location: response.location ?? fallback.location,
});

const QualifyingQuestionsPage = () => {
  const [searchParams] = useSearchParams();

  const leadId = useMemo(
    () => firstQueryValue(searchParams, ["lead_id", "leadId"]),
    [searchParams]
  );
  const hasLeadId = leadId.length > 0;
  const hasValidLeadId = isUuid(leadId);

  const initialLeadIdentity = useMemo<LeadIdentity>(
    () => ({
      leadNumber: firstQueryValue(searchParams, ["lead_number", "leadNumber"]),
      firstName: firstQueryValue(searchParams, ["first_name", "firstName"]),
      lastName: firstQueryValue(searchParams, ["last_name", "lastName"]),
      email: firstQueryValue(searchParams, ["email"]),
    }),
    [searchParams]
  );

  const initialForm = useMemo<QualifyingQuestionsForm>(
    () => ({
      currentVisa: firstQueryValue(searchParams, ["current_visa", "currentVisa"]),
      timeline: firstQueryValue(searchParams, ["timeline"]),
      goals: firstQueryValue(searchParams, ["goals"]),
      location: firstQueryValue(searchParams, ["location"]),
    }),
    [searchParams]
  );

  const [leadIdentity, setLeadIdentity] = useState<LeadIdentity>(initialLeadIdentity);
  const [form, setForm] = useState<QualifyingQuestionsForm>(initialForm);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [locked, setLocked] = useState(false);
  const [submittedAt, setSubmittedAt] = useState<string | null>(null);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [submitError, setSubmitError] = useState<string | null>(null);
  const [submitSuccess, setSubmitSuccess] = useState<string | null>(null);

  const hydrateFromApi = useCallback(
    (response: LeadQualifyingQuestionsResponse) => {
      setLeadIdentity({
        leadNumber: response.lead_number || initialLeadIdentity.leadNumber,
        firstName: response.first_name || initialLeadIdentity.firstName,
        lastName: response.last_name || initialLeadIdentity.lastName,
        email: response.email || initialLeadIdentity.email,
      });
      setForm((prev) => mapResponseToForm(response, prev));
      setLocked(response.submitted);
      setSubmittedAt(response.submitted_at);
    },
    [initialLeadIdentity.email, initialLeadIdentity.firstName, initialLeadIdentity.lastName, initialLeadIdentity.leadNumber]
  );

  const fetchQualifyingQuestions = useCallback(async () => {
    if (!hasValidLeadId) return;
    setLoading(true);
    setLoadError(null);
    try {
      const response = await qualifyingQuestionsService.getLeadQualifyingQuestions(leadId);
      hydrateFromApi(response);
    } catch (error) {
      setLoadError(extractApiError(error, "Could not load lead details."));
    } finally {
      setLoading(false);
    }
  }, [hasValidLeadId, hydrateFromApi, leadId]);

  useEffect(() => {
    void fetchQualifyingQuestions();
  }, [fetchQualifyingQuestions]);

  const fullName = `${leadIdentity.firstName} ${leadIdentity.lastName}`.trim();
  const submittedAtLabel = formatSubmittedAt(submittedAt);
  const isFormComplete =
    form.currentVisa.trim().length >= 2 &&
    form.timeline.trim().length >= 2 &&
    form.goals.trim().length >= 2 &&
    form.location.trim().length >= 2;

  const isSaveDisabled =
    !hasValidLeadId || loading || saving || locked || !isFormComplete;

  const updateField = <K extends keyof QualifyingQuestionsForm>(
    field: K,
    value: QualifyingQuestionsForm[K]
  ) => {
    setForm((prev) => ({ ...prev, [field]: value }));
    if (submitError) setSubmitError(null);
    if (submitSuccess) setSubmitSuccess(null);
  };

  const handleSubmit = async () => {
    if (!hasLeadId) {
      setSubmitError("Missing lead_id query parameter.");
      return;
    }
    if (!hasValidLeadId) {
      setSubmitError("lead_id query parameter must be a valid UUID.");
      return;
    }
    if (!isFormComplete) {
      setSubmitError(
        "Please complete current visa, timeline, goals, and location (minimum 2 characters each)."
      );
      return;
    }
    if (locked) {
      setSubmitError("This form was already submitted and is now read-only.");
      return;
    }

    setSaving(true);
    setSubmitError(null);
    setSubmitSuccess(null);
    try {
      const response = await qualifyingQuestionsService.submitLeadQualifyingQuestions(
        leadId,
        {
          current_visa: form.currentVisa,
          timeline: form.timeline,
          goals: form.goals,
          location: form.location,
        }
      );
      hydrateFromApi(response);
      setSubmitSuccess("Thanks. Your qualifying questions have been submitted.");
    } catch (error) {
      const message = extractApiError(error, "Could not save qualifying questions.");
      setSubmitError(message);
      if (message.toLowerCase().includes("already")) {
        await fetchQualifyingQuestions();
      }
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="login-page">
      <div className="login-shell">
        <aside className="login-brand card">
          <div className="logo" role="img" aria-label="CaseFlow" />
          <span className="badge badge-teal login-badge">
            <i className="ti ti-help-hexagon" /> Qualifying questionnaire
          </span>
          <div className="login-brand-copy">
            <h1 className="login-title">Help Us Assess Your Consultation Fit</h1>
            <p className="login-subtitle">
              Share your current visa status, plans, goals, and location so our adviser can review
              suitability for consultation.
            </p>
          </div>

          <div className="login-points">
            <div className="login-point">
              <i className="ti ti-passport" />
              <div>
                <div className="login-point-title">Current visa context</div>
                <div className="login-point-sub">
                  We use this to assess your current immigration position.
                </div>
              </div>
            </div>
            <div className="login-point">
              <i className="ti ti-clock-hour-4" />
              <div>
                <div className="login-point-title">Your timeline and goals</div>
                <div className="login-point-sub">
                  This helps prioritize urgency and the right engagement path.
                </div>
              </div>
            </div>
            <div className="login-point">
              <i className="ti ti-lock-check" />
              <div>
                <div className="login-point-title">One-time confirmation</div>
                <div className="login-point-sub">
                  After submission, your answers are locked and shown as read-only.
                </div>
              </div>
            </div>
          </div>
        </aside>

        <section className="login-form-card card">
          <div className="card-hd">
            <i className="ti ti-list-check" /> Qualifying questions
          </div>
          <p className="login-form-copy">
            Complete all fields and save. Required fields are marked with <strong>*</strong>.
          </p>

          {!hasLeadId || !hasValidLeadId ? (
            <section className="notice notice-red">
              <i className="ti ti-alert-triangle" />
              <span>
                {!hasLeadId
                  ? "Missing lead_id query param. Use /qualifying-questions?lead_id=<lead-uuid>."
                  : "Invalid lead_id query param. It must be a valid UUID."}
              </span>
            </section>
          ) : null}

          {loadError ? (
            <section className="notice notice-red">
              <i className="ti ti-alert-triangle" />
              <span>{loadError}</span>
            </section>
          ) : null}

          {submitError ? (
            <section className="notice notice-red">
              <i className="ti ti-alert-triangle" />
              <span>{submitError}</span>
            </section>
          ) : null}

          {submitSuccess ? (
            <section className="notice notice-green">
              <i className="ti ti-check" />
              <span>{submitSuccess}</span>
            </section>
          ) : null}

          {locked ? (
            <section className="notice notice-blue">
              <i className="ti ti-lock" />
              <span>
                This questionnaire is already submitted and cannot be edited.
                {submittedAtLabel ? ` Submitted on ${submittedAtLabel}.` : ""}
              </span>
            </section>
          ) : null}

          <section className="login-role-meta">
            <strong>Lead details</strong>
            <p>
              Name: <strong>{fullName || "Not provided"}</strong>
            </p>
            <p>
              Email: <strong>{leadIdentity.email || "Not provided"}</strong>
            </p>
            <p>
              Lead number: <strong>{leadIdentity.leadNumber || "Not provided"}</strong>
            </p>
            <p>
              Lead ID: <strong>{leadId || "Not provided"}</strong>
            </p>
          </section>

          <form
            className="signup-form"
            onSubmit={(event) => {
              event.preventDefault();
              void handleSubmit();
            }}
          >
            <div className="field">
              <label htmlFor="qualifying-current-visa">Current visa *</label>
              <input
                id="qualifying-current-visa"
                placeholder="e.g. Visitor visa"
                value={form.currentVisa}
                disabled={locked || loading}
                onChange={(event) => updateField("currentVisa", event.target.value)}
              />
            </div>

            <div className="field">
              <label htmlFor="qualifying-timeline">Timeline *</label>
              <input
                id="qualifying-timeline"
                placeholder="e.g. Need to apply in 2 months"
                value={form.timeline}
                disabled={locked || loading}
                onChange={(event) => updateField("timeline", event.target.value)}
              />
            </div>

            <div className="field">
              <label htmlFor="qualifying-goals">Goals *</label>
              <textarea
                id="qualifying-goals"
                rows={4}
                placeholder="Tell us your immigration goals."
                value={form.goals}
                disabled={locked || loading}
                onChange={(event) => updateField("goals", event.target.value)}
              />
            </div>

            <div className="field">
              <label htmlFor="qualifying-location">Current location *</label>
              <input
                id="qualifying-location"
                placeholder="e.g. Auckland, NZ"
                value={form.location}
                disabled={locked || loading}
                onChange={(event) => updateField("location", event.target.value)}
              />
            </div>

            <div className="assistant-modal-actions">
              <button className="btn btn-primary btn-sm" type="submit" disabled={isSaveDisabled}>
                <i className="ti ti-device-floppy" />
                {saving ? "Saving..." : "Save"}
              </button>
            </div>
          </form>
        </section>
      </div>
    </div>
  );
};

export default QualifyingQuestionsPage;
